import { HandIcon } from '@c/icons';
import ConfirmModal from '@c/modals/ConfirmModal';
import CounterOfferModal from '@c/modals/CounterOfferModal';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import Button from '@ui/Button';
import Chip from '@ui/Chip';
import SafeImage from '@ui/SafeImage';
import { ProductDocument } from '@util/types/firestore/products';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import Link from 'next/link';
import React, { useEffect } from 'react';
import {
  acceptOffer,
  getOffer,
  setOfferState,
  counterOffer,
} from '@util/firestore/offers/';
import { getProductById } from '@util/firestore/products';
import { useRouter } from 'next/router';
import { formatCurrency } from '@util/index';
import { logEvent, getItems } from '@util/analytics';
import { googleDataLayerPurchase } from '@util/analytics/google.service';
import { updateOrderByKey } from '@util/firestore/orders';
import { logError } from '@util/logError';
import { getProductUrl } from '@util/urlHelpers';

interface MessageOfferProps {
  offer_id?: string;
}

// Firebase callable rejections carry a code formatted as "functions/<code>".
// Only these deliberately-user-authored business-rejection codes are safe to
// surface verbatim to the buyer — anything else (including the "unknown"
// catch-all) falls back to a generic message so no internal/technical detail
// leaks to the UI.
const SAFE_TO_DISPLAY_CODES = new Set([
  'functions/failed-precondition',
  'functions/not-found',
  'functions/permission-denied',
  'functions/already-exists',
  'functions/invalid-argument',
  'functions/unavailable',
]);

const GENERIC_ACCEPT_ERROR =
  'Something went wrong completing this offer. Please refresh and check its status.';

// acceptOffer (gearfocus-api functions/src/offers/index.ts) throws
// failed-precondition with exactly these authored messages. Any other
// failed-precondition message on that call is Stripe's own text from the
// charge catch ("Your card was declined.") — the buyer's payment failed.
// Shown to the seller verbatim it reads as if the seller's card failed.
const AUTHORED_FAILED_PRECONDITION_MESSAGES = new Set([
  'A required field is missing',
  'This offer is no longer pending',
]);

export const SELLER_PAYMENT_FAILED_MESSAGE =
  "The buyer's payment didn't go through, so this offer has closed. We've let the buyer know.";

export function getAcceptOfferErrorMessage(
  e: unknown,
  isBuyer: boolean
): string {
  const code = (e as { code?: string })?.code;
  if (!code || !SAFE_TO_DISPLAY_CODES.has(code) || !(e instanceof Error)) {
    return GENERIC_ACCEPT_ERROR;
  }
  if (
    !isBuyer &&
    code === 'functions/failed-precondition' &&
    !AUTHORED_FAILED_PRECONDITION_MESSAGES.has(e.message)
  ) {
    return SELLER_PAYMENT_FAILED_MESSAGE;
  }
  return e.message;
}

export default function MessageOffer({ offer_id }: MessageOfferProps) {
  const [loadingOfferSubmit, setLoadingOfferSubmit] = React.useState(false);

  const { userDoc } = useAuth();
  const { showErrorToast } = useToastContext();
  const router = useRouter();
  const [countdownTimer, setCountdownTimer] = React.useState<number | null>(
    null
  );
  const queryClient = useQueryClient();

  const timeToString = (time: number) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = time % 60;
    return `${hours}h : ${minutes}m : ${
      seconds < 10 ? '0' + seconds : seconds
    }s`;
  };

  const { data: offer } = useQuery({
    queryKey: ['offer', offer_id],
    queryFn: async () => offer_id ? await getOffer(offer_id) : null,
    enabled: !!offer_id,
  });
  const { data: product } = useQuery({
    queryKey: ['product', offer?.product_id],
    queryFn: async () => offer?.product_id ? await getProductById(offer.product_id) : null,
    enabled: !!offer?.product_id,
  });

  useEffect(() => {
    if (!offer?.time_remaining || offer.state || countdownTimer) return;
    const offerExpirationDate = new Date(offer.time_remaining).getTime();
    // get the time remaining in seconds
    const timeRemaining = Math.floor(
      (offerExpirationDate - new Date().getTime()) / 1000
    );

    setCountdownTimer(timeRemaining);
    const interval = setInterval(() => {
      setCountdownTimer((prev) => {
        if (prev && prev > 0) {
          return prev - 1;
        }
        clearInterval(interval);
        return 0;
      });
    }, 1000);
  }, [offer, countdownTimer]);

  const getChip = (state: number) => {
    switch (state) {
      case 0:
        return (
          <Chip text={timeToString(countdownTimer ?? 0)} color="primary" />
        );
      case 1:
        return <Chip text="Accepted" color="success" />;
      case 2:
        return <Chip text="Declined" color="error" />;
      case 3:
        return <Chip text="Cancelled" color="gray" />;
      default:
        return <Chip text="Pending" color="gray" />;
    }
  };

  const declineOffer = async () => {
    if (!offer_id) return;
    await setOfferState(offer_id, 2);
    queryClient.invalidateQueries({
      queryKey: ['offer', offer_id],
    });
    setCountdownTimer(null);
  };

  const acceptThatOffer = async () => {
    if (!offer_id || !product) return;
    setLoadingOfferSubmit(true);
    try {
      const order = await acceptOffer(offer_id);

      // From here on the buyer has been charged and the order exists.
      // Treat everything below as best-effort: a failure here must never
      // be reported to the user as a failed purchase.
      try {
        const offerQ = queryClient.invalidateQueries({
          queryKey: ['offer', offer_id],
        });
        const orderQ = queryClient.invalidateQueries({
          queryKey: ['orders', userDoc?.uid],
        });
        await Promise.all([offerQ, orderQ]);

        // Build items array for tracking (same for both dataLayer and TIE)
        const trackingItems = getItems([product]);

        // GA4 + Google Ads + Meta CAPI tracking via dataLayer
        const payload = {
          currency: 'USD',
          transaction: {
            id: order.id,
            sellers: [], // Make Offer is single seller
            total: order.total ?? 0,
            tax: order.tax ?? 0,
            shipping: order.shipping_total ?? 0,
          },
          items: trackingItems,
          customer: {
            name: '', // Order doesn't have shipping details at this point
            email: offer?.buyer_email || userDoc?.email || '',
            phone: null,
            country: '',
            region: '',
            city: '',
            zip: '',
          },
          userId: userDoc?.uid,
        };
        googleDataLayerPurchase(payload, { clearAfter: true });

        // TIE/Revenue Roll + FB Pixel tracking (uses same nested payload)
        logEvent('purchase', payload, order.id);

        // Mark analytics as fired (persistent cross-session dedup)
        await updateOrderByKey(order.id, 'analytics_fired', true);
      } catch (analyticsError) {
        // Non-fatal: the purchase already succeeded. Log only, no toast.
        logError(analyticsError);
      }

      // Buyers go to Purchases, Sellers go to Orders
      const dashboardPath = isBuyer ? 'purchases' : 'orders';
      router.push(`/dashboard/${dashboardPath}/${order.id}`);
      setCountdownTimer(null);
    } catch (e: unknown) {
      logError(e);
      queryClient.invalidateQueries({ queryKey: ['offer', offer_id] });
      showErrorToast(getAcceptOfferErrorMessage(e, isBuyer));
    } finally {
      setLoadingOfferSubmit(false);
      setAcceptIsOpen(false);
    }
  };

  const [isOpen, setIsOpen] = React.useState(false);
  const [isAcceptOpen, setAcceptIsOpen] = React.useState(false);
  const [isCounterOpen, setIsCounterOpen] = React.useState(false);

  const isSeller = userDoc?.uid === offer?.seller_id;
  const isBuyer = userDoc?.uid === offer?.buyer_id;

  // Check if it's a counter offer from the seller that buyer can respond to
  const isSellerCounter =
    offer?.current_offer_by === 'seller' &&
    offer?.pending_response_from === 'buyer';

  // Determine who can take action
  const sellerCanAct = isSeller && !offer?.state && !isSellerCounter;
  const buyerCanActOnCounter = isBuyer && !offer?.state && isSellerCounter;

  const handleCounterSubmit = async (newPrice: number, message?: string) => {
    if (!offer_id) return;
    await counterOffer(offer_id, newPrice, message);
    queryClient.invalidateQueries({
      queryKey: ['offer', offer_id],
    });
    setCountdownTimer(null);
  };

  return (
    <>
      <div className="flex w-full flex-col gap-[1.6rem] p-[1.6rem]">
        {product && offer && (
          <>
            <Link
              href={getProductUrl(product.slug)}
              className="flex min-h-[6.4rem] items-center gap-[1.6rem]"
            >
              <SafeImage
                className="rounded-2xl"
                height={64}
                width={64}
                alt={product.title}
                key={product.images?.[0]?.thumb}
                src={product.images?.[0]?.thumb}
              />
              <div className="text-elipsis flex grow flex-col items-start  justify-center gap-[1.6rem] overflow-hidden whitespace-nowrap">
                <h2 className="line-clamp-2 whitespace-pre-wrap font-semibold">
                  {product.title}
                </h2>
                <div className="flex  w-full flex-wrap items-start gap-[0.8rem]">
                  <div className="flex flex-grow flex-col gap-[0.4rem]">
                    <div className="flex items-center">
                      <span className="text-brand-dark-gray">
                        Listed price:
                      </span>
                      <span className="ml-2 font-semibold">
                        {formatCurrency(product.price ?? 0)}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-brand-dark-gray">
                        {offer.counter_history && offer.counter_history.length > 0
                          ? 'Counter offer:'
                          : 'Offer price:'}
                      </span>
                      <span className="ml-2 font-semibold">
                        {formatCurrency(offer.price ?? 0)}
                      </span>
                    </div>
                  </div>
                  {getChip(offer.state)}
                </div>
              </div>
            </Link>
            <div className="flex items-start gap-[0.8rem]">
              {/* Seller actions on buyer's offer */}
              {sellerCanAct && (
                <div className="flex w-full justify-center gap-[1.6rem]">
                  <Button
                    type="error"
                    text="Decline"
                    width="fixed"
                    onClick={() => setIsOpen(true)}
                  />
                  <Button
                    type="tertiary"
                    width="fixed"
                    text="Counter"
                    onClick={() => setIsCounterOpen(true)}
                  />
                  <Button
                    type="success"
                    width="fixed"
                    text="Accept"
                    onClick={() => setAcceptIsOpen(true)}
                  />
                </div>
              )}
              {/* Buyer actions on seller's counter */}
              {buyerCanActOnCounter && (
                <div className="flex w-full justify-center gap-[1.6rem]">
                  <Button
                    type="error"
                    text="Decline"
                    width="fixed"
                    onClick={() => setIsOpen(true)}
                  />
                  <Button
                    type="tertiary"
                    width="fixed"
                    text="Counter"
                    onClick={() => setIsCounterOpen(true)}
                  />
                  <Button
                    type="success"
                    width="fixed"
                    text="Accept"
                    onClick={() => setAcceptIsOpen(true)}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <ConfirmModal
        title="Decline Offer?"
        body="Are you sure you want to decline this offer? You can not undo this action."
        buttonText="Decline Offer"
        isOpen={isOpen}
        dismiss={(confirm) => {
          setIsOpen(false);
          if (confirm) declineOffer();
        }}
      />
      <ConfirmModal
        title={buyerCanActOnCounter ? 'Accept Counter Offer?' : 'Accept Offer?'}
        icon={<HandIcon />}
        btnType="success"
        body={
          buyerCanActOnCounter
            ? `By accepting this counter offer, you agree to purchase this item for ${formatCurrency(offer?.total ?? 0)} (${formatCurrency(offer?.price ?? 0)} + ${formatCurrency(offer?.shipping_cost ?? 0)} Shipping + ${formatCurrency(offer?.tax ?? 0)} Tax). Your payment method will be charged.`
            : `By accepting this offer, you agree to sell this item for ${formatCurrency(offer?.total ?? 0)} (${formatCurrency(offer?.price ?? 0)} + ${formatCurrency(offer?.shipping_cost ?? 0)} Shipping + ${formatCurrency(offer?.tax ?? 0)} Tax). An order will be created and can be viewed in your seller dashboard.`
        }
        buttonText={buyerCanActOnCounter ? 'Accept Counter' : 'Accept Offer'}
        isOpen={isAcceptOpen}
        loading={loadingOfferSubmit}
        dismiss={(confirm) => {
          if (confirm) acceptThatOffer();
          else setAcceptIsOpen(false);
        }}
      />
      {offer && product && (
        <CounterOfferModal
          isOpen={isCounterOpen}
          offer={offer}
          productPrice={product.price}
          isSeller={isSeller}
          onSubmit={handleCounterSubmit}
          dismiss={() => setIsCounterOpen(false)}
        />
      )}
    </>
  );
}
